import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import cs from 'classnames';

import { selectActiveSidebarTabId } from 'features/sidebar/sidebarSelectors';
import { setActiveSidebarTabId } from 'features/sidebar/sidebarSlice';

import { useAppDispatch, useAppSelector } from 'hooks/useTypedRedux';

import { SidebarTabsItem } from 'types/sidebar';

import styles from './SidebarTabs.module.scss';

interface SidebarTabsProps {
  items: SidebarTabsItem[];
  isChangeCurrentTabOnNewItems?: boolean;
  children?:
    | React.ReactElement
    | React.ReactElement[]
    | string
    | null
    | undefined;
  openItemId?: number;
}

const SidebarTabs: FC<SidebarTabsProps> = ({
  items,
  isChangeCurrentTabOnNewItems = true,
  children,
  openItemId
}) => {
  const { t } = useTranslation();
  // this is a workaround to change active tab item from actions
  const activeTabItemIdFromStore = useAppSelector(selectActiveSidebarTabId);
  const dispatch = useAppDispatch();

  const getInitialItemId = (): number | null => {
    if (openItemId !== undefined) {
      return openItemId;
    }
    if (items.length > 0) {
      return items[0].id;
    }
    return null;
  };

  const [currentItemId, setCurrentItemId] = useState<number | null>(
    getInitialItemId()
  );

  // Sync with openItemId only when openItemId changes itself.
  useEffect(() => {
    if (openItemId !== undefined) {
      setCurrentItemId(openItemId);
      dispatch(setActiveSidebarTabId(null));
    }
  }, [openItemId, dispatch]);

  // Reset to the first tab when items change — only if openItemId is not set.
  useEffect(() => {
    if (!items.length || openItemId !== undefined) {
      return;
    }

    if (isChangeCurrentTabOnNewItems) {
      setCurrentItemId(items[0].id);
      dispatch(setActiveSidebarTabId(null));
    }
  }, [items, openItemId, isChangeCurrentTabOnNewItems, dispatch]);

  const currentItemIdToOpen = activeTabItemIdFromStore || currentItemId;

  const currentComponent =
    items.find((el) => el.id === currentItemIdToOpen)?.component || null;

  const handleChangeCurrentItem = (itemId: number): void => {
    setCurrentItemId(itemId);
    dispatch(setActiveSidebarTabId(null));
  };

  return (
    <div className={styles.container}>
      <div className={styles.tabsViewport}>
        <div className={styles.tabsWrapper}>
          <div className={styles.tabs}>
            {items.map((item) => (
              <div
                className={cs(
                  styles.item,
                  currentItemIdToOpen === item.id ? styles.active : ''
                )}
                key={item.id}
                onClick={() => handleChangeCurrentItem(item.id)}
              >
                {item.icon}
                {t(item.title)}
              </div>
            ))}
          </div>
        </div>
      </div>
      {children && <div className={styles.children}>{children}</div>}
      <div className={styles.content}>{currentComponent}</div>
    </div>
  );
};

export default SidebarTabs;
